import { capitalizeCompanyName } from "../utils/formatting";

const normalizeString = (value, fallback = "") =>
  typeof value === "string" ? value : fallback;

const normalizeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const getLoadIdentifier = (load) => {
  if (!load) {
    return null;
  }
  return (
    load.id ??
    load.entry_id ??
    load.code ??
    load.reference ??
    load.load_id ??
    null
  );
};

export const normalizeLoad = (raw = {}) => {
  const id = getLoadIdentifier(raw);
  return {
    ...raw,
    id,
    origin_city: normalizeString(raw.origin_city).trim(),
    origin_state: normalizeString(raw.origin_state).trim(),
    destination_city: normalizeString(raw.destination_city).trim(),
    destination_state: normalizeString(raw.destination_state).trim(),
    length: normalizeNumber(raw.length, normalizeNumber(raw.equipment_length)),
    weight: normalizeNumber(raw.weight),
    rate_per_mile_est: normalizeNumber(raw.rate_per_mile_est),
    distance: normalizeNumber(raw.distance),
    dead_head: normalizeNumber(raw.dead_head ?? raw.origin_distance),
    origin_distance: normalizeNumber(raw.origin_distance ?? raw.dead_head),
    payment: normalizeNumber(raw.payment ?? raw.rate),
    score: normalizeNumber(raw.score),
    age: normalizeNumber(raw.age),
    truck_type: normalizeString(raw.truck_type || raw.equipment_type || "V"),
    truck_types: Array.isArray(raw.truck_types) ? raw.truck_types : [],
    company_email: normalizeString(
      raw.company_email || raw.email || raw.contact_email,
    ),
    company_name: capitalizeCompanyName(normalizeString(raw.company_name)),
    company_mc: normalizeString(raw.company_mc),
    company_dot: normalizeString(raw.company_dot),
    contact_name: normalizeString(raw.contact_name),
    contact_phone: normalizeString(raw.contact_phone || raw.phone_number),
    ship_date: normalizeString(raw.ship_date),
    delivery_date: normalizeString(raw.delivery_date),
    pickup_time: normalizeString(raw.pickup_time),
    delivery_time: normalizeString(raw.delivery_time),
    equipment_type: normalizeString(
      raw.equipment_type || raw.truck_type || "V",
    ),
    equipment_description: normalizeString(raw.equipment_description),
    data_source: normalizeString(raw.data_source || raw.source || "truckstop"),
    entry_id: raw.entry_id ?? (id ? `${raw.user_email || "load"}-${id}` : null),
    isSaved: Boolean(raw.isSaved),
  };
};

export const buildSavePayload = (load) => {
  const normalized = normalizeLoad(load);
  return {
    id: normalized.id,
    entry_id: normalized.entry_id,
    origin_city: normalized.origin_city,
    origin_state: normalized.origin_state,
    destination_city: normalized.destination_city,
    destination_state: normalized.destination_state,
    length: normalized.length,
    weight: normalized.weight,
    rate_per_mile_est: normalized.rate_per_mile_est,
    dead_head: normalized.dead_head,
    age: normalized.age,
    ship_date:
      normalized.ship_date || new Date().toISOString().split("T")[0],
    delivery_date: normalized.delivery_date,
    pickup_time: normalized.pickup_time,
    delivery_time: normalized.delivery_time,
    distance: normalized.distance,
    contact_name: normalized.contact_name,
    contact_phone: normalized.contact_phone,
    company_email: normalized.company_email,
    company_name: normalized.company_name,
    company_mc: normalized.company_mc,
    company_dot: normalized.company_dot,
    description: normalizeString(normalized.description),
    score: normalized.score,
    equipment_type: normalized.equipment_type,
    equipment_description: normalized.equipment_description,
    data_source: normalized.data_source,
    load_type: normalizeString(normalized.load_type),
    stops: normalized.stops,
    credit_rating: normalized.credit_rating,
    notes: normalized.notes,
    origin_distance: normalized.origin_distance,
    destination_distance: normalized.destination_distance,
    payment: normalized.payment,
    longitude: normalized.longitude,
    latitude: normalized.latitude,
    route_order: normalized.route_order,
    sourceCostPerUnit: normalized.sourceCostPerUnit,
    units: normalized.units,
    code: normalized.code,
    cost_type: normalized.cost_type,
    cost_description: normalized.cost_description,
    pay_rate: normalized.pay_rate || normalized.rate_per_mile_est,
    other_trailer_types: normalized.other_trailer_types,
    green_light: Boolean(normalized.green_light),
    full_load: normalized.full_load !== false,
    receive_date: normalized.receive_date,
    phone_number: normalized.contact_phone,
    fuel_cost: normalized.fuel_cost,
    special_instructions: normalized.special_instructions,
    is_pair: normalized.is_pair,
    child: normalized.child,
  };
};
